import styled, { CSSObject, keyframes, css } from "styled-components";
import { Popup } from "@swiggy-private/dls-web-core/dist/components/Popup";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

const PopupWidth = "min(calc(100% - 32px), 400px)";
const BUTTON_HEIGHT = "44px";

export { PopupWidth };

export const UnlockPopupContainer = styled(Popup)(() => ({
    position: "relative" as CSSObject["position"],
    border: "none",
}));

export const UnlockPopupCloseIcon = styled.div(({ theme }) => ({
    width: `calc(${theme.spacing.large} + ${theme.spacing.xxSmall})`,
    height: `calc(${theme.spacing.large} + ${theme.spacing.xxSmall})`,
    borderRadius: "50%",
    position: "absolute",
    top: theme.spacing.medium,
    right: theme.spacing.medium,
    cursor: "pointer",
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

export const UnlockPopupContentContainer = styled.div(({ theme }) => ({
    padding: theme.spacing.large,
    display: "flex",
    flexDirection: "column" as CSSObject["flexDirection"],
    alignItems: "center",
    gap: theme.spacing.medium,
    textAlign: "center",
    justifyContent: "center",
    paddingTop: theme.spacing.xLarge,
}));

export const UnlockPopupFoodIconContainer = styled.span(({ theme }) => ({
    width: "fit-content",
    height: "fit-content",
    margin: `1px ${theme.spacing.xxSmall} 0 0`,
    verticalAlign: "middle",
}));

export const UnlockPopupDetails = styled.div(() => ({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "70%",
    textAlign: "left" as CSSObject["textAlign"],
}));

export const UnlockPopupFlexContainer = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "row" as CSSObject["flexDirection"],
    alignItems: "center",
    gap: theme.spacing.xxxSmall,
}));

export const UnlockPopupBasePriceContainer = styled(Text)(() => ({
    textDecoration: "line-through",
    textDecorationLine: "line-through",
}));

export const UnlockPopupFinalPriceContainer = styled(Text)(() => ({}));

export const UnlockPopupItemCardContainer = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "row" as CSSObject["flexDirection"],
    alignItems: "flex-start",
    gap: theme.spacing.small,
    width: "100%",
    padding: theme.spacing.medium,
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
    borderRadius: theme.cornerRadius.medium,
    position: "relative",
    minHeight: `calc(${theme.spacing.x16} * 2 + ${theme.spacing.xSmall})`,
    justifyContent: "center",
}));

export const UnlockPopupImageContainer = styled.div(({ theme }) => {
    const width = `calc(${theme.spacing.x16} * 2 + ${theme.spacing.x4} + ${theme.spacing.xxxSmall} + 1px)`;
    const height = `calc(${theme.spacing.x20} * 2)`;
    const borderRadius = theme.spacing.xSmall;
    const border = `1px solid ${theme.colors[theme.colorMode].border_Secondary}`;

    return {
        position: "relative",
        borderRadius,
        width,
        height,
        margin: 0,
        flexShrink: 0,
        alignSelf: "flex-start",
        minHeight: height,
        "> img": {
            width: "100%",
            height: `calc(100% - ${theme.spacing.xxxSmall})`,
            objectFit: "cover",
            borderRadius,
            border,
        },
    };
});

export const UnlockPopupTextContainer = styled.div(() => ({
    flex: 0.5,
    padding: 0,
    width: "auto",
    minWidth: 0,
    alignSelf: "center" as CSSObject["alignSelf"],
    display: "flex",
    flexDirection: "column" as CSSObject["flexDirection"],
    justifyContent: "flex-start",
    margin: 0,
}));

const scrollDown = keyframes`
    0% {
        transform: translateY(0);
    }
    20% {
        transform: translateY(0);
    }
    25% {
        transform: translateY(-14px);
    }
    45% {
        transform: translateY(-14px);
    }
    50% {
        transform: translateY(-28px);
    }
    70% {
        transform: translateY(-28px);
    }
    75% {
        transform: translateY(-42px);
    }
    95% {
        transform: translateY(-42px);
    }
    100% {
        transform: translateY(-56px);
    }
`;

export const UnlockPopupTickerWrapper = styled.div`
    position: relative;
    width: ${(props) => `calc(${props.theme.spacing.xLarge} * 2 + ${props.theme.spacing.xxxSmall} + 1px)`};
    height: ${(props) => `calc(${props.theme.spacing.medium} - ${props.theme.spacing.xxxSmall})`};
    flex-shrink: 0;
    overflow: hidden;
    margin-bottom: ${(props) => props.theme.spacing.xxxSmall};
`;

export const UnlockPopupTickerContent = styled.div<{ shouldAnimate?: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ shouldAnimate }) =>
        shouldAnimate &&
        css`
            animation: ${scrollDown} 6s linear infinite;
        `}
`;

export const UnlockPopupTickerItem = styled(Text)`
    line-height: ${(props) => props.theme.spacing.medium};
    white-space: nowrap;
    flex-shrink: 0;
    height: ${(props) => `calc(${props.theme.spacing.medium} - ${props.theme.spacing.xxxSmall})`};
    display: flex;
    align-items: center;
`;

export const UnlockPopupName = styled(Text)(({ theme }) => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "auto",
    maxWidth: "100%",

    marginBottom: theme.spacing.xxSmall,
}));

export const UnlockPopupButtonContainer = styled.div(({ theme }) => ({
    display: "flex",
    height: BUTTON_HEIGHT,
    padding: theme.spacing.medium,
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing.xxxSmall,
    alignSelf: "stretch",
    borderRadius: theme.cornerRadius.medium,
    backgroundColor: theme.colors[theme.colorMode].primary,
    width: "100%",
    border: "none",
    cursor: "pointer",
    "& span": {
        fontSize: `${theme.spacing.medium} !important`,
    },
}));

export const UnlockPopupDecorativeLine = styled.div(({ theme }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: `calc((${theme.spacing.medium} - 1px) * -1)`,
    marginBottom: `calc(${theme.spacing.xxSmall} * -1)`,
    "& > img": {
        width: "85%",
        height: "auto",
        objectFit: "cover" as CSSObject["objectFit"],
    },
}));

export const UnlockMessageText = styled(Text)(({ theme }) => ({
    textAlign: "center" as CSSObject["textAlign"],
    whiteSpace: "pre-line",
    marginBottom: `calc(${theme.spacing.large} + 1px)`,
}));

export const UnlockPopupLockIconContainer = styled.div(({ theme }) => ({
    position: "absolute",
    right: `calc(${theme.spacing.xxSmall} * -1)`,
    top: `calc(${theme.spacing.xxxSmall} * -1)`,
    width: `calc(${theme.spacing.large} + ${theme.spacing.xxSmall})`,
    height: `calc(${theme.spacing.large} + ${theme.spacing.xxSmall})`,
    borderRadius: `calc(${theme.cornerRadius.xxSmall} + ${theme.cornerRadius.xxxSmall})`,
    border: `1px solid ${theme.colors[theme.colorMode].border_Secondary}`,
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:after": {
        content: '""',
        position: "absolute",
        top: `-${theme.spacing.xxxSmall}`,
        right: `-${theme.spacing.xxSmall}`,
        bottom: `-${theme.spacing.xxxSmall}`,
        left: `-${theme.spacing.xxSmall}`,
    },
}));
